import { useState } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Code, Trash2, AlertTriangle, ShieldAlert, CheckCircle } from "lucide-react";

export default function DeveloperPage() {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handlePurge = async () => {
    if (!password) return toast.error("Masukkan password developer");
    setLoading(true);
    try {
      const { data } = await api.post("/developer/purge-customers", { password });
      setResult(data);
      setConfirmOpen(false);
      setPassword("");
      toast.success("Data pelanggan berhasil dihapus");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="p-6 md:p-8" data-testid="developer-page">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-white">Developer</h1>
        <p className="text-gray-400 text-sm mt-1">Tools & aksi developer. Hati-hati, aksi di halaman ini bersifat permanen.</p>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Purge Customers */}
        <div className="bg-[#0F0F0F] border border-red-900/30 p-6" data-testid="purge-section">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-red-900/20 flex items-center justify-center shrink-0">
              <ShieldAlert size={20} className="text-red-500" />
            </div>
            <div className="flex-1">
              <h2 className="text-white font-bold text-base mb-1">Hapus Semua Data Pelanggan</h2>
              <p className="text-gray-400 text-sm mb-1">Menghapus secara permanen:</p>
              <ul className="text-gray-500 text-xs space-y-0.5 mb-4 list-disc list-inside">
                <li>Semua akun pelanggan (customer)</li>
                <li>Semua transaksi POS</li>
                <li>Semua kupon loyalitas</li>
                <li>Semua testimonial</li>
                <li>Semua data refund</li>
                <li>Semua antrian</li>
                <li>Semua laporan harian</li>
                <li>Semua log notifikasi WhatsApp</li>
              </ul>
              <p className="text-red-400 text-xs font-bold mb-4 flex items-center gap-1">
                <AlertTriangle size={12} /> Aksi ini TIDAK BISA dibatalkan!
              </p>
              <Button onClick={() => { setResult(null); setPassword(""); setConfirmOpen(true); }} className="bg-red-700 hover:bg-red-600 text-white font-bold uppercase rounded-none h-10" data-testid="purge-btn">
                <Trash2 size={14} className="mr-2" /> Hapus Semua Data Pelanggan
              </Button>
            </div>
          </div>
        </div>

        {/* Result */}
        {result && (
          <div className="bg-green-950/30 border border-green-800/30 p-5" data-testid="purge-result">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle size={16} className="text-green-400" />
              <p className="text-green-400 font-bold text-sm">{result.message}</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(result.deleted || {}).map(([key, val]) => (
                <div key={key} className="bg-[#0A0A0A] border border-[#1A1A1A] p-3 text-center">
                  <p className="text-white font-bold text-lg">{val}</p>
                  <p className="text-gray-500 text-[10px] uppercase">{key.replace(/_/g, " ")}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Confirm Dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-[#0F0F0F] border-red-900/50 text-white max-w-sm rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase text-red-500 flex items-center gap-2">
              <AlertTriangle size={20} /> Konfirmasi Penghapusan
            </DialogTitle>
            <DialogDescription className="text-gray-400 text-sm">
              Semua data pelanggan, transaksi, kupon, dan data terkait akan dihapus permanen. Masukkan password developer untuk melanjutkan.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-gray-300 text-xs font-bold uppercase">Password Developer</Label>
              <Input
                type="password"
                className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none focus:border-red-500"
                placeholder="Masukkan password developer..."
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handlePurge()}
                data-testid="dev-password-input"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handlePurge} disabled={loading || !password} className="flex-1 bg-red-700 hover:bg-red-600 text-white font-bold uppercase rounded-none h-10" data-testid="confirm-purge-btn">
                <Trash2 size={14} className="mr-2" /> {loading ? "Menghapus..." : "Hapus Permanen"}
              </Button>
              <Button onClick={() => setConfirmOpen(false)} variant="ghost" className="text-gray-400 hover:text-white rounded-none h-10 px-4">
                Batal
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
